import { Button, Col, Form, Input, message, Row, Select, Spin, Upload, Segmented, Alert } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { BASE_API } from '../../axios';
import { getUserDetails, makeGetCall } from '../../utils/helper';
import { TokenStorage } from '../../utils/cookieStorage';
import { useForm } from 'antd/es/form/Form';
import ErrorBoundary from '../../components/ErrorBoundary';
import axios from 'axios';
import { createDocument, getEditorConfig, downloadDocumentBlob, deleteDocument, forceSaveDocument } from '../OnlyOfficeDocuments/documentsApi';
import useOnlyOfficeScript from '../OnlyOfficeDocuments/useOnlyOfficeScript';

const { Option } = Select;

interface AddTemplateOodocsProps {
  onSuccess?: () => void;
}

const AddTemplateOodocs = ({ onSuccess }: AddTemplateOodocsProps = {}) => {
  const [templateForm] = useForm();
  const [modalities, setModalities] = useState([]);
  const [bodyPartsList, setBodyPartsList] = useState([]);
  const [selectedModality, setSelectedModality] = useState(null);
  const [fileList, setFileList] = useState<any[]>([]);
  const [submitInProgress, setSubmitInProgress] = useState(false);
  const [loadingState, setLoadingState] = useState({ modalities: false, bodyParts: false, error: null });

  // Editor mode: 'editor' or 'upload'
  const [mode, setMode] = useState<string>('editor');

  // OnlyOffice editor state
  const [tempDocId, setTempDocId] = useState<number | null>(null);
  const [editorConfig, setEditorConfig] = useState<any>(null);
  const [onlyOfficeUrl, setOnlyOfficeUrl] = useState<string | null>(null);
  const [editorLoading, setEditorLoading] = useState(false);
  const [editorError, setEditorError] = useState<string | null>(null);
  const editorInstanceRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { loaded: scriptLoaded, error: scriptError, retry: retryScript } = useOnlyOfficeScript(onlyOfficeUrl);

  const getModalities = useCallback(() => {
    setLoadingState(prev => ({ ...prev, modalities: true, error: null }));
    makeGetCall('/modalities')
      .then(res => {
        setModalities(res.data?.data || []);
        setLoadingState(prev => ({ ...prev, modalities: false }));
      })
      .catch(e => {
        const errorMessage = e.response?.data?.message || e.message || 'Failed to load modalities';
        setLoadingState(prev => ({ ...prev, modalities: false, error: errorMessage }));
        message.error(`Error loading modalities: ${errorMessage}`);
      });
  }, []);

  const getBodyParts = useCallback(() => {
    setLoadingState(prev => ({ ...prev, bodyParts: true, error: null }));
    makeGetCall('/body-parts')
      .then(res => {
        setBodyPartsList(res.data?.data || []);
        setLoadingState(prev => ({ ...prev, bodyParts: false }));
      })
      .catch(e => {
        const errorMessage = e.response?.data?.message || e.message || 'Failed to load body parts';
        setLoadingState(prev => ({ ...prev, bodyParts: false, error: errorMessage }));
        message.error(`Error loading body parts: ${errorMessage}`);
      });
  }, []);

  useEffect(() => {
    getModalities();
    getBodyParts();
  }, [getModalities, getBodyParts]);

  // ── Create temp document & load editor when mode is 'editor' ──
  useEffect(() => {
    if (mode !== 'editor' || tempDocId) return;

    let cancelled = false;
    setEditorLoading(true);
    setEditorError(null);

    createDocument({ title: `_template_draft_${Date.now()}` })
      .then(async (res) => {
        if (cancelled) return;
        const docId = res.data?.data?.id || res.data?.data?.doc_id;
        if (!docId) throw new Error('Failed to create temporary document');
        setTempDocId(docId);

        const configRes = await getEditorConfig(docId);
        if (cancelled) return;
        const { config, onlyOfficeUrl: url } = configRes.data.data;
        setEditorConfig(config);
        setOnlyOfficeUrl(url);
      })
      .catch((err) => {
        if (cancelled) return;
        setEditorError(err.response?.data?.message || err.message || 'Failed to initialize editor');
        setEditorLoading(false);
      });

    return () => { cancelled = true; };
  }, [mode]);

  // ── Initialize OnlyOffice editor once script + config are ready ──
  useEffect(() => {
    if (!scriptLoaded || !editorConfig || !containerRef.current) return;

    const timer = setTimeout(() => {
      try {
        editorInstanceRef.current = new (window as any).DocsAPI.DocEditor(
          'oo-add-template-editor',
          {
            ...editorConfig,
            events: {
              ...editorConfig.events,
              onDocumentReady: () => setEditorLoading(false),
              onError: (event: any) => {
                console.error('OnlyOffice editor error:', event);
                message.error('Editor encountered an error');
              },
            },
          },
        );
      } catch (err) {
        console.error('Failed to initialize OnlyOffice editor:', err);
        setEditorError('Failed to initialize editor');
        setEditorLoading(false);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [scriptLoaded, editorConfig]);

  // ── Cleanup editor on unmount ──
  useEffect(() => {
    return () => {
      if (editorInstanceRef.current) {
        try { editorInstanceRef.current.destroyEditor(); } catch {}
        editorInstanceRef.current = null;
      }
      // Clean up temp document
      if (tempDocId) {
        deleteDocument(tempDocId).catch(() => {});
      }
    };
  }, [tempDocId]);

  const handleModalityChange = useCallback((value) => {
    setSelectedModality(value);
    templateForm.setFieldsValue({ bodypart: undefined });
  }, [templateForm]);

  const filteredBodyParts = bodyPartsList.filter((bodyPart: any) =>
    !selectedModality || bodyPart.modality === selectedModality
  );

  const submitTemplate = useCallback(async (file: Blob | File) => {
    const values = templateForm.getFieldsValue();
    const formData = new FormData();
    formData.append('modality', values.modality);
    formData.append('bodypart', values.bodypart);
    formData.append('template_name', values.template_name);
    formData.append('display_name', values.display_name);
    formData.append('file', file, `${values.template_name || 'template'}.docx`);

    const token = TokenStorage.getAccessToken();
    const csrfToken = sessionStorage.getItem('csrf_token');
    const res = await axios.post(`${BASE_API}/create-template-oodocs`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(csrfToken ? { 'x-csrf-token': csrfToken } : {}),
      },
      timeout: 60000,
    });
    return res;
  }, [templateForm]);

  const onSubmit = useCallback(async () => {
    try {
      await templateForm.validateFields();
    } catch {
      return;
    }

    if (mode === 'upload' && fileList.length === 0) {
      message.error('Please upload a .docx template file');
      return;
    }

    if (mode === 'editor' && !tempDocId) {
      message.error('Editor is not ready yet');
      return;
    }

    setSubmitInProgress(true);
    try {
      let file: Blob | File;

      if (mode === 'editor') {
        // Trigger server-side forcesave via OnlyOffice Command Service
        await forceSaveDocument(tempDocId);
        // Wait for OnlyOffice to process the callback and save the file
        await new Promise(resolve => setTimeout(resolve, 3000));
        // Download the saved .docx blob
        file = await downloadDocumentBlob(tempDocId);
      } else {
        file = fileList[0].originFileObj || fileList[0];
      }

      const res = await submitTemplate(file);

      if (res.data?.success) {
        message.success('Template created successfully');
        templateForm.resetFields();
        setFileList([]);
        setSelectedModality(null);

        // Destroy editor and clean up temp doc
        if (editorInstanceRef.current) {
          try { editorInstanceRef.current.destroyEditor(); } catch {}
          editorInstanceRef.current = null;
        }
        if (tempDocId) {
          deleteDocument(tempDocId).catch(() => {});
          setTempDocId(null);
          setEditorConfig(null);
        }

        onSuccess?.();
      } else {
        message.error(res.data?.message || 'Failed to create template');
      }
    } catch (e: any) {
      message.error(e.response?.data?.message || e.message || 'Failed to create template');
    } finally {
      setSubmitInProgress(false);
    }
  }, [templateForm, fileList, mode, tempDocId, submitTemplate, onSuccess]);

  const uploadProps = {
    beforeUpload: (file: any) => {
      const isDocx = file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        || file.name?.endsWith('.docx');
      if (!isDocx) {
        message.error('Only .docx files are allowed');
        return Upload.LIST_IGNORE;
      }
      return false;
    },
    onChange: ({ fileList: newFileList }: any) => {
      setFileList(newFileList.slice(-1));
    },
    fileList,
    maxCount: 1,
    accept: '.docx',
  };

  const renderEditor = () => {
    if (scriptError) {
      return (
        <Alert
          type="error"
          message="Failed to load editor"
          description={scriptError}
          action={<Button onClick={retryScript}>Retry</Button>}
        />
      );
    }
    if (editorError) {
      return (
        <Alert
          type="error"
          message="Editor Error"
          description={editorError}
          action={
            <Button onClick={() => {
              setEditorError(null);
              setTempDocId(null);
              setEditorConfig(null);
            }}>
              Retry
            </Button>
          }
        />
      );
    }

    return (
      <div style={{ position: 'relative', height: 'calc(100vh - 380px)', minHeight: 300, border: '1px solid #d9d9d9', borderRadius: 6 }}>
        {editorLoading && (
          <div style={{ position: 'absolute', inset: 0, zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.15)' }}>
            <Spin size="large" tip="Loading editor..." />
          </div>
        )}
        <div
          id="oo-add-template-editor"
          ref={containerRef}
          style={{ height: '100%', width: '100%' }}
        />
      </div>
    );
  };

  return (
    <ErrorBoundary>
      <Spin
        spinning={submitInProgress || loadingState.modalities || loadingState.bodyParts}
        tip={submitInProgress ? 'Saving template...' : 'Loading data...'}
      >
        <div className="admin-form-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {loadingState.error && (
            <div className="error-container">
              <strong>Error:</strong> {loadingState.error}
              <Button
                type="link"
                size="small"
                onClick={() => { getModalities(); getBodyParts(); }}
                className="retry-button"
              >
                Retry
              </Button>
            </div>
          )}
          <div className="add-template-main-container" style={{ maxWidth: 1200, width: '100%' }}>
            <div className="admin-form-header">
              <h1 className="admin-form-title">Create New Template (.docx)</h1>
            </div>
            <Form form={templateForm} onFinish={onSubmit} className="modern-form" layout="vertical">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Modality"
                    name="modality"
                    rules={[{ required: true, message: 'Please select modality!' }]}
                  >
                    <Select placeholder="Select modality" onChange={handleModalityChange}>
                      {modalities?.map((m: any) => (
                        <Option key={m.code || m.value} value={m.code || m.value}>{m.label}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Body Part"
                    name="bodypart"
                    rules={[{ required: true, message: 'Please select body part!' }]}
                  >
                    <Select
                      placeholder={selectedModality ? 'Select body part' : 'Select modality first'}
                      disabled={!selectedModality}
                    >
                      {filteredBodyParts?.map((bp: any) => (
                        <Option key={bp.code || bp.value} value={bp.code || bp.value}>{bp.label}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Template Name"
                    name="template_name"
                    rules={[{ required: true, message: 'Please input template name!' }]}
                  >
                    <Input placeholder="Enter template name (internal)" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Display Name"
                    name="display_name"
                    rules={[{ required: true, message: 'Please input display name!' }]}
                  >
                    <Input placeholder="Enter display name (user-facing)" />
                  </Form.Item>
                </Col>
              </Row>

              <div style={{ marginBottom: 8 }}>
                <label style={{ fontWeight: 500, marginBottom: 8, fontSize: '0.875rem' }}>Template Content</label>
                <Segmented
                  options={[
                    { label: 'Create in Editor', value: 'editor' },
                    { label: 'Upload .docx File', value: 'upload' },
                  ]}
                  value={mode}
                  onChange={(val) => setMode(val as string)}
                  style={{ marginBottom: 12 }}
                />

                {mode === 'editor' ? renderEditor() : (
                  <Upload {...uploadProps}>
                    <Button icon={<UploadOutlined />}>Select .docx File</Button>
                  </Upload>
                )}
              </div>

              <Form.Item className="text-center" style={{ marginTop: 12 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  className="admin-create-btn"
                  size="large"
                  loading={submitInProgress}
                >
                  Create Template
                </Button>
              </Form.Item>
            </Form>
          </div>
        </div>
      </Spin>
    </ErrorBoundary>
  );
};

export default AddTemplateOodocs;
